import React, { useMemo, useState, useEffect, useRef, memo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { createColumns } from './columns/columnDefs';
import { useTableContext } from './context/TableContext';

/**
 * TaskTable - TanStack Table implementation
 * 
 * Features:
 * - Row selection
 * - Column resizing with persistence (shared across all tables via context)
 * - Sticky columns (checkbox + name)
 * - Row expansion for subtasks
 * - Category grouping
 * - Memoized cells for performance
 */
const TaskTable = memo(({ 
  tasks, 
  category,
  groupId,
  onSelectionChange,
}) => {
  // Get shared column sizing and visibility from context (managed at TableViewNew level)
  const { onAddTask, onAddDecision, onAddWait, onRenameCategory, columnSizing, onColumnSizingChange, columnVisibility, lockStates, customFields } = useTableContext();
  
  // Column definitions (memoized, filtered by visibility, includes custom field columns)
  const columns = useMemo(() => createColumns(columnVisibility, customFields), [columnVisibility, customFields]);

  // Row selection state
  const [rowSelection, setRowSelection] = useState({});

  // Expanded rows state (for subtasks)
  const [expanded, setExpanded] = useState({});

  // Category header editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(category?.name || '');
  const titleInputRef = useRef(null);

  // Collapsed state
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sync title value when category changes
  useEffect(() => {
    if (!isEditingTitle && category?.name) {
      setTitleValue(category.name);
    }
  }, [category?.name, isEditingTitle]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Notify parent of selection changes
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    onSelectionChange?.(groupId, selectedIds);
  }, [rowSelection, onSelectionChange, groupId]);

  // Sync DOM with React selection state (for external updates like select all)
  // This ensures the DOM reflects the selection state when it changes
  useEffect(() => {
    // Get all row elements in this table
    const tableRows = document.querySelectorAll(`[data-row-id]`);
    tableRows.forEach(row => {
      const rowId = row.getAttribute('data-row-id');
      if (rowSelection[rowId]) {
        row.classList.add('table-view__row--selected');
      } else {
        row.classList.remove('table-view__row--selected');
      }
    });
  }, [rowSelection]);

  // Create table instance
  const table = useReactTable({
    data: tasks,
    columns,
    state: {
      columnSizing: columnSizing || {},
      rowSelection,
      expanded,
    },
    onColumnSizingChange: onColumnSizingChange, // Use shared handler from context
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    // Disable auto-reset behaviors to prevent infinite loops when data changes
    autoResetAll: false,
  });

  // Calculate sticky left positions
  const checkboxWidth = table.getColumn('select')?.getSize() || 48;
  
  // Get rows for rendering
  const rows = table.getRowModel().rows;

  // Category title handlers
  const handleTitleClick = () => {
    if (!category || category.isUncategorized || !onRenameCategory) return;
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    if (!category) return;
    const trimmed = titleValue.trim();
    if (trimmed && trimmed !== category.name) {
      onRenameCategory?.(category.name, trimmed);
    } else {
      setTitleValue(category.name);
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setTitleValue(category?.name || '');
      setIsEditingTitle(false);
    }
  };

  return (
    <div className="table-view__group" style={{ '--category-color': category?.color || '#6b7280' }}>
      {/* Group Header - same for all modes (flat list or swimlanes) */}
      <div className="table-view__group-header">
        <div className="table-view__group-header-sticky">
          <button
            className="table-view__toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <span className={`table-view__arrow ${!isCollapsed ? 'table-view__arrow--expanded' : ''}`}>
              ▶
            </span>
          </button>

          {isEditingTitle && !category?.isUncategorized ? (
            <input
              ref={titleInputRef}
              type="text"
              className="table-view__group-title-input"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={handleTitleKeyDown}
            />
          ) : (
            <h3
              className={`table-view__group-title ${!category?.isUncategorized && onRenameCategory ? 'table-view__group-title--editable' : ''}`}
              onClick={handleTitleClick}
              title={!category?.isUncategorized && onRenameCategory ? 'Click to rename' : undefined}
            >
              {category?.isUncategorized ? 'Uncategorized' : category?.name}
            </h3>
          )}
        </div>

        {/* Add buttons */}
        <div className="table-view__group-header-actions">
          <button
            className="table-view__add-btn"
            onClick={() => onAddTask?.(category?.isUncategorized ? null : category?.name)}
            title="Add mission"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Mission
          </button>
          <button
            className="table-view__add-btn table-view__add-btn--decision"
            onClick={() => onAddDecision?.(category?.isUncategorized ? null : category?.name)}
            title="Add decision"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L22 12L12 22L2 12L12 2Z" />
            </svg>
            Decision
          </button>
          <button
            className="table-view__add-btn"
            onClick={() => onAddWait?.(category?.isUncategorized ? null : category?.name)}
            title="Add wait for"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2h12" />
              <path d="M6 22h12" />
              <path d="M7 2c0 5 5 7 5 10s-5 5-5 10" />
              <path d="M17 2c0 5-5 7-5 10s5 5 5 10" />
            </svg>
            Wait for
          </button>
        </div>
      </div>

      {/* Table Content */}
      {!isCollapsed && (
        <div className="table-view__table">
          {/* Table Header */}
          <div className="table-view__table-header">
            {table.getHeaderGroups().map((headerGroup) => (
              <React.Fragment key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = header.column.columnDef.meta?.sticky;
                  const stickyLeft = header.column.id === 'select' 
                    ? 0 
                    : header.column.id === 'name' 
                      ? checkboxWidth 
                      : undefined;

                  return (
                    <div
                      key={header.id}
                      className={`table-view__col table-view__col--${header.column.id} ${
                        isSticky ? 'table-view__col--sticky' : ''
                      } ${header.column.getCanResize() ? 'table-view__col--resizable' : ''}`}
                      style={{
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        ...(isSticky && { left: stickyLeft }),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}

                      {/* Column Resizer */}
                      {header.column.getCanResize() && (
                        <div
                          className={`table-view__resizer ${
                            header.column.getIsResizing() ? 'table-view__resizer--active' : ''
                          }`}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {/* Table Body */}
          <div className="table-view__table-body">
            {rows.map((row) => {
              const isSubtask = row.depth > 0;
              const task = row.original;
              const isSelected = row.getIsSelected();
              const isCompleted = task.status === 'completed';
              const isArchived = task.archived;
              const lockState = lockStates?.[task.id];
              const isLocked = lockState?.isLocked && task.status !== 'completed';
              const isExcluded = lockState?.isExcluded;

              // NOTE: 'table-view__row--selected' is NOT included here - it's managed via DOM manipulation
              // in CheckboxCell for instant visual feedback. React state still tracks selection
              // for other purposes (bulk actions, etc.) but the visual class is DOM-driven.
              return (
                <div
                  key={row.id}
                  data-row-id={task.id}
                  className={`table-view__row ${
                    isSubtask ? 'table-view__row--subtask' : ''
                  } ${isCompleted ? 'table-view__row--completed' : ''} ${
                    isArchived ? 'table-view__row--archived' : ''
                  } ${isExcluded ? 'table-view__row--excluded' : ''} ${isLocked ? 'table-view__row--locked' : ''}`}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = cell.column.columnDef.meta?.sticky;
                    const stickyLeft = cell.column.id === 'select'
                      ? 0
                      : cell.column.id === 'name'
                        ? checkboxWidth
                        : undefined;

                    return (
                      <div
                        key={cell.id}
                        className={`table-view__cell table-view__cell--${cell.column.id} ${
                          isSticky ? 'table-view__cell--sticky' : ''
                        }`}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          ...(isSticky && { left: stickyLeft }),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for TaskTable memo
  return prevProps.groupId === nextProps.groupId &&
         prevProps.category?.name === nextProps.category?.name &&
         prevProps.category?.color === nextProps.category?.color &&
         prevProps.tasks === nextProps.tasks &&
         prevProps.onSelectionChange === nextProps.onSelectionChange;
});

export default TaskTable;

